import React, { useContext } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

import { ConfigLinksDetailsContext } from '../Context/cofingLinksDetailsContext';
import LatestNewsWidget from '../Components/LatestNewsWidget';

// ─── Main Component ──────────────────────────────────────────────────────────
const GuestPortal = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const { configLinks } = useContext(ConfigLinksDetailsContext);

  const municipalityName = isRTL
    ? configLinks?.municipalityNameAr || t('guest_heroTitle')
    : configLinks?.municipalityNameEn || t('guest_heroTitle');

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>

      {/* ── Hero Section ──────────────────────────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${alpha(theme.palette.primary.light, 0.9)} 100%)`,
          color: '#fff',
          py: { xs: 8, md: 13 },
          px: 2,
          borderRadius: { xs: 3, md: 4 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', top: -80, left: -80, width: 260, height: 260, borderRadius: '50%', background: alpha('#fff', 0.04), pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -100, right: -60, width: 320, height: 320, borderRadius: '50%', background: alpha('#fff', 0.06), pointerEvents: 'none' }} />

        <Container maxWidth="md" sx={{ position: 'relative' }}>
          <Typography variant="h2" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '2rem', md: '3rem' }, letterSpacing: -0.5 }}>
            {municipalityName}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.88, fontWeight: 400, mb: 4, maxWidth: 560, mx: 'auto', lineHeight: 1.7 }}>
            {t('guest_heroSubtitle')}
          </Typography>

          {/* Quick action buttons */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="outlined" size="large" onClick={() => navigate('/services-guide')}
              startIcon={<MenuBookIcon />}
              sx={{ borderColor: alpha('#fff', 0.7), color: '#fff', fontWeight: 700, borderRadius: 3, px: 4, textTransform: 'none', fontSize: '1rem', '&:hover': { borderColor: '#fff', bgcolor: alpha('#fff', 0.08) } }}
            >
              {t('nav_servicesGuide')}
            </Button>
            <Button
              variant="contained" size="large" onClick={() => navigate('/guest-services')}
              startIcon={<ReportProblemIcon />}
              sx={{ bgcolor: '#fff', color: theme.palette.primary.main, fontWeight: 700, borderRadius: 3, px: 4, textTransform: 'none', fontSize: '1rem', '&:hover': { bgcolor: alpha('#fff', 0.9) } }}
            >
              {t('guest_submitComplaint_btn', 'تقديم شكوى')}
            </Button>
          </Stack>

          {/* Login hint */}
          <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ color: alpha('#fff', 0.85), fontWeight: 500 }}>
              {isRTL ? 'هل لديك حساب؟' : 'Already have an account?'}
            </Typography>
            <Button
              size="medium"
              startIcon={<LoginIcon />}
              onClick={() => navigate('/login')}
              sx={{
                color: '#fff', fontWeight: 700, textTransform: 'none', fontSize: '1rem',
                bgcolor: alpha('#fff', 0.14), borderRadius: 2, px: 3, py: 1,
                '&:hover': { bgcolor: alpha('#fff', 0.22) },
              }}
            >
              {t('login')}
            </Button>
            <Button
              size="medium"
              startIcon={<PersonAddIcon />}
              onClick={() => navigate('/sign-up')}
              sx={{
                color: '#fff', fontWeight: 700, textTransform: 'none', fontSize: '1rem',
                bgcolor: 'transparent', border: '2px solid rgba(255,255,255,0.18)', borderRadius: 2, px: 3, py: 1,
                '&:hover': { bgcolor: alpha('#fff', 0.08) },
              }}
            >
              {t('signup', 'إنشاء حساب')}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── Latest News ── */}
      <LatestNewsWidget />

    </Box>
  );
};

export default GuestPortal;
